import { Payload } from "@neworbit/redux-helpers";
import * as moment from "moment";
import { CalendarView } from "@teamprevent/common/calendar";
import { loadAndTrack } from "redux-request-loading";
import axios from "axios";
import { NonFixedLengthChildAppointment } from "@teamprevent/common/calendar/models";

import { Dispatch } from "redux";

import { calendarApi } from "../calendarApi";

import {
    ClinicDetails,
    ClinicCreateModel,
    ClinicErrors,
    EditClinicCliniciansErrors,
    MobileClinicCreateModel,
    AddUnknownEmployeeModel,
    CloneClinicResult,
    EditClinicModel,
    BlockClinicModel,
    CloneClinicRequest
} from "./models";

import * as actionTypes from "./actionTypes";

import { clinicApi } from "./clinicApi";

import { push } from "redux-little-router";

export type ClinicAction =
    ({ type: typeof actionTypes.LOAD_CLINICS_SUCCESS } & Payload<ClinicDetails[]>) |
    ({ type: typeof actionTypes.LOAD_CLINIC_SUCCESS } & Payload<ClinicDetails>) |
    ({ type: typeof actionTypes.CREATE_CLINIC_SUCCESS } & Payload<ClinicDetails>) |
    ({ type: typeof actionTypes.CREATE_CLINIC_ERROR } & Payload<ClinicErrors>) |
    ({ type: typeof actionTypes.EDIT_CLINIC_CLINICIANS_SUCCESS } & Payload<ClinicDetails>) |
    ({ type: typeof actionTypes.EDIT_CLINIC_CLINICIANS_ERROR } & Payload<EditClinicCliniciansErrors>) |
    ({ type: typeof actionTypes.EDIT_CLINIC_SUCCESS } & Payload<ClinicDetails>) |
    ({ type: typeof actionTypes.BLOCK_CLINIC_SUCCESS } & Payload<ClinicDetails>) |
    ({ type: typeof actionTypes.ADD_RECALL_CASES_TO_MOBILE_CLINIC_SUCCESS } & Payload<ClinicDetails>) |
    ({ type: typeof actionTypes.ADD_RECALL_CASE_TO_MOBILE_CLINIC_SUCCESS } & Payload<{ clinicId: string; appointment: NonFixedLengthChildAppointment }>);

export const loadClinicsSuccess = (payload: ClinicDetails[]): ClinicAction => ({
    payload,
    type: actionTypes.LOAD_CLINICS_SUCCESS
});

export const loadClinicSuccess = (payload: ClinicDetails): ClinicAction => ({
    payload,
    type: actionTypes.LOAD_CLINIC_SUCCESS
});

export const createClinicSuccess = (payload: ClinicDetails): ClinicAction => ({
    payload,
    type: actionTypes.CREATE_CLINIC_SUCCESS
});

export const editClinicCliniciansSuccess = (payload: ClinicDetails): ClinicAction => ({
    payload,
    type: actionTypes.EDIT_CLINIC_CLINICIANS_SUCCESS
});

export const createClinicError = (payload: ClinicErrors): ClinicAction => ({
    payload,
    type: actionTypes.CREATE_CLINIC_ERROR
});

export const editClinicCliniciansError = (payload: EditClinicCliniciansErrors): ClinicAction => ({
    payload,
    type: actionTypes.EDIT_CLINIC_CLINICIANS_ERROR
});

export const editClinicSuccess = (payload: ClinicDetails): ClinicAction => ({
    payload,
    type: actionTypes.EDIT_CLINIC_SUCCESS
});

export const blockClinicSuccess = (payload: ClinicDetails): ClinicAction => ({
    payload,
    type: actionTypes.BLOCK_CLINIC_SUCCESS
});

export const addOhCasesToMobileClinicSuccess = (payload: ClinicDetails): ClinicAction => ({
    payload,
    type: actionTypes.ADD_RECALL_CASES_TO_MOBILE_CLINIC_SUCCESS
});

export const addOhCaseToMobileClinicSuccess = (payload: { clinicId: string; appointment: NonFixedLengthChildAppointment }): ClinicAction => ({
    payload,
    type: actionTypes.ADD_RECALL_CASE_TO_MOBILE_CLINIC_SUCCESS
});

export const loadClinics = (
    showOnlyClinicsWithInsufficientClinicians: boolean,
    clinicType: string,
    clinicLocationType: string,
    startDate: moment.Moment,
    teamIds: string[],
    customerIds: string[],
    professionIds: string[],
    view: CalendarView,
    restrictToAppointmentId?: string
) => {
    const endDate = startDate.clone().add(1, view);
    return async (dispatch: Dispatch) => {
        try {
            const result = await loadAndTrack(
                dispatch,
                actionTypes.LOAD_CLINICS,
                clinicApi.getInDateRangeWithFilters(
                    startDate,
                    endDate,
                    showOnlyClinicsWithInsufficientClinicians,
                    clinicType,
                    clinicLocationType,
                    teamIds,
                    customerIds,
                    professionIds,
                    restrictToAppointmentId
                )
            );
            dispatch(loadClinicsSuccess(result));
        } catch (ex) {
            if (!(ex instanceof axios.Cancel)) { // eslint-disable-line import/no-named-as-default-member
                throw ex;
            }
        }
    };
};

export const loadClinic = ({ clinicId }: { clinicId: string }) => {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(
            dispatch,
            actionTypes.LOAD_CLINIC,
            clinicApi.get(clinicId)
        );
        dispatch(loadClinicSuccess(result));
    };
};

export const loadClinicsAndDistances = (
    showOnlyClinicsWithInsufficientClinicians: boolean,
    clinicType: string,
    clinicLocationType: string,
    startDate: moment.Moment,
    view: CalendarView,
    postcode: string,
    distance: number
) => {
    const endDate = startDate.clone().add(1, view);
    return async (dispatch: Dispatch) => {
        try {
            return await loadAndTrack(
                dispatch,
                actionTypes.LOAD_CLINICS,
                clinicApi.getDistancesInDateRangeWithFilters(
                    startDate,
                    endDate,
                    showOnlyClinicsWithInsufficientClinicians,
                    clinicType,
                    clinicLocationType,
                    postcode,
                    distance
                )
            );
        } catch (ex) {
            if (!(ex instanceof axios.Cancel)) { // eslint-disable-line import/no-named-as-default-member
                throw ex;
            }

            return null;
        }
    };
};

export const createClinic = (basePath: string, clinic: ClinicCreateModel) => {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actionTypes.CREATE_CLINIC, clinicApi.createClinic(clinic));
        if ((result as ClinicErrors).errors) {
            dispatch(createClinicError(result as ClinicErrors));
            return;
        }

        dispatch(push(basePath));
        dispatch(createClinicSuccess(result as ClinicDetails));
    };
};

export const createMobileClinic = (basePath: string, clinic: MobileClinicCreateModel) => {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actionTypes.CREATE_CLINIC, clinicApi.createMobileClinic(clinic));
        if ((result as ClinicErrors).errors) {
            dispatch(createClinicError(result as ClinicErrors));
            return;
        }

        dispatch(push(basePath));
        dispatch(createClinicSuccess(result as ClinicDetails));
    };
};

export const addUnknownEmployeeToClinic = (clinicId: string, model: AddUnknownEmployeeModel) => {
    return async (dispatch: Dispatch) => {
        const addResult = await loadAndTrack(dispatch, actionTypes.ADD_UNKNOWN_EMPLOYEE_MODEL, clinicApi.addUnknownEmployeeToClinic(clinicId, model));
        const refreshResult = await loadAndTrack(
            dispatch,
            actionTypes.LOAD_CLINIC,
            clinicApi.get(clinicId)
        );
        dispatch(loadClinicSuccess(refreshResult));
        return addResult;
    };
};

export const searchForEmployeeFromCustomer = (clinicId: string, employeeName: string) =>
    async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actionTypes.SEARCH_FOR_EMPLOYEE, clinicApi.searchForEmployee(clinicId, employeeName));
        return result;
    };

export const cloneClinic = (cloneRequest: CloneClinicRequest) =>
    (dispatch: Dispatch) =>
        loadAndTrack<CloneClinicResult[]>(dispatch, actionTypes.CLONE_CLINIC, clinicApi.cloneClinic(cloneRequest));

export const editClinicClinicians = (basePath: string, clinicId: string, clinicianIds: string[]) =>
    async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actionTypes.EDIT_CLINIC_CLINICIANS, clinicApi.updateClinicClinicians(clinicId, clinicianIds));
        if ((result as ClinicErrors).errors) {
            dispatch(editClinicCliniciansError(result as ClinicErrors));
            return;
        }

        dispatch(push(basePath));
        dispatch(editClinicCliniciansSuccess(result as ClinicDetails));
    };

export const editClinic = (clinicId: string, editClinicModel: EditClinicModel) =>
    async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actionTypes.EDIT_CLINIC, clinicApi.editClinic(clinicId, editClinicModel));
        if (result.success) {
            dispatch(editClinicSuccess(result.clinic));
        }
    };

export const blockClinic = (clinicId: string, model: BlockClinicModel) => {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actionTypes.BLOCK_CLINIC, clinicApi.blockClinic(clinicId, model));
        if (result.success) {
            dispatch(blockClinicSuccess(result.clinic));
        }
    };
};

export const markClinicAppointmentAsArrived = (appointmentId: string ) => {
    return async (dispatch: Dispatch) => {
        try {
            const result = await loadAndTrack(
                dispatch,
                actionTypes.LOAD_CLINIC,
                calendarApi.markAsArrived(appointmentId)
            );
            dispatch(loadClinicSuccess(result));
        } catch (ex) {
            if (!(ex instanceof axios.Cancel)) { // eslint-disable-line import/no-named-as-default-member
                throw ex;
            }
        }
    };
};

export const addOhCaseToMobileClinic = (customerId: string, clinicId: string, employeeRecordId: string, ohCaseId: string) => {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(
            dispatch,
            actionTypes.ADD_RECALL_CASE_TO_MOBILE_CLINIC,
            clinicApi.addRecallCaseToMobileClinic(customerId, clinicId, employeeRecordId, ohCaseId)
        );
        dispatch(addOhCaseToMobileClinicSuccess({
            clinicId,
            appointment: result
        }));
    };
};
